/**
 * SkinGlow ad attribution (sitewide).
 * Last paid click wins: any tagged landing overwrites the stored value, which
 * then survives 30 days across untagged pages. Mirrored to a first-party
 * cookie because Instagram's in-app browser and ITP both evict localStorage.
 * Keep deriveAdChannel rules aligned with apps/skg-commerce/src/lib/attribution.ts.
 */
(function () {
  var VERSION = 2;
  // The theme and the app extension both ship this file; first one wins.
  if (window.SKGAttribution && window.SKGAttribution.version === VERSION) return;

  var STORAGE_KEY = "skg_attr_v1";
  var TTL_MS = 30 * 24 * 60 * 60 * 1000;
  var META_SOURCES = { facebook: 1, fb: 1, ig: 1, instagram: 1, meta: 1 };
  var META_REFERRER_HOSTS = /(^|\.)(instagram\.com|facebook\.com|fb\.com|fb\.me|messenger\.com)$/;
  var GOOGLE_REFERRER_HOSTS = /(^|\.)google(\.[a-z]{2,3})+$/;

  function norm(value) {
    return String(value || "")
      .trim()
      .toLowerCase();
  }

  function deriveAdChannel(utm) {
    var u = utm || {};
    var source = norm(u.source);
    var medium = norm(u.medium);
    var gclid = norm(u.gclid);
    var fbclid = norm(u.fbclid);
    var ttclid = norm(u.ttclid);
    var msclkid = norm(u.msclkid);

    if (gclid) return "google";
    if (fbclid) return "meta";
    if (ttclid) return "tiktok";
    if (msclkid) return "bing";

    if (source.indexOf("google") !== -1 || medium.indexOf("google") !== -1) {
      return "google";
    }
    if (
      META_SOURCES[source] ||
      source.indexOf("facebook") !== -1 ||
      source.indexOf("instagram") !== -1
    ) {
      return "meta";
    }
    if (source.indexOf("tiktok") !== -1) {
      return "tiktok";
    }
    if (source.indexOf("snap") !== -1) {
      return "snapchat";
    }
    if (
      source.indexOf("bing") !== -1 ||
      source.indexOf("microsoft") !== -1 ||
      (medium.indexOf("cpc") !== -1 && source.indexOf("bing") !== -1)
    ) {
      return "bing";
    }
    if (source) {
      var cleaned = source.replace(/[^a-z0-9_-]+/g, "_").replace(/^_+|_+$/g, "");
      return cleaned || "direct";
    }
    return "direct";
  }

  /** Inferred channel for untagged visits. Empty string when unusable. */
  function deriveChannelFromReferrer(referrer) {
    var host = "";
    try {
      if (!referrer) return "";
      host = new URL(referrer).hostname.toLowerCase();
    } catch (e) {
      return "";
    }
    if (!host) return "";
    // Internal navigation is not a referral.
    if (typeof location !== "undefined" && host === String(location.hostname).toLowerCase()) {
      return "";
    }
    if (META_REFERRER_HOSTS.test(host)) return "meta";
    if (GOOGLE_REFERRER_HOSTS.test(host)) return "google_organic";
    return "referral";
  }

  function omitEmpty(obj) {
    var out = {};
    Object.keys(obj || {}).forEach(function (key) {
      var value = obj[key];
      if (value == null) return;
      var str = String(value).trim();
      if (!str) return;
      out[key] = str;
    });
    return out;
  }

  function parseUrlAttribution(search, href) {
    var params;
    try {
      params = new URLSearchParams(search || "");
    } catch (e) {
      return null;
    }
    var raw = {
      source: params.get("utm_source") || "",
      medium: params.get("utm_medium") || "",
      campaign: params.get("utm_campaign") || "",
      term: params.get("utm_term") || "",
      content: params.get("utm_content") || "",
      gclid: params.get("gclid") || "",
      fbclid: params.get("fbclid") || "",
      ttclid: params.get("ttclid") || "",
      msclkid: params.get("msclkid") || "",
    };
    var utm = omitEmpty(raw);
    if (!Object.keys(utm).length) return null;
    utm.channel = deriveAdChannel(utm);
    utm.derived_from = utm.gclid || utm.fbclid || utm.ttclid || utm.msclkid ? "click_id" : "utm";
    return {
      capturedAt: Date.now(),
      landingUrl: href || "",
      utm: utm,
      channel: utm.channel,
    };
  }

  function readCookie() {
    try {
      var parts = String(document.cookie || "").split(";");
      for (var i = 0; i < parts.length; i++) {
        var part = parts[i].trim();
        if (part.indexOf(STORAGE_KEY + "=") !== 0) continue;
        var parsed = JSON.parse(decodeURIComponent(part.slice(STORAGE_KEY.length + 1)));
        if (!parsed || typeof parsed !== "object") return null;
        return parsed;
      }
      return null;
    } catch (e) {
      return null;
    }
  }

  function writeCookie(stash) {
    try {
      var secure = typeof location !== "undefined" && location.protocol === "https:" ? "; Secure" : "";
      document.cookie =
        STORAGE_KEY +
        "=" +
        encodeURIComponent(JSON.stringify(stash)) +
        "; Max-Age=" +
        Math.floor(TTL_MS / 1000) +
        "; Path=/; SameSite=Lax" +
        secure;
    } catch (e) {
      /* cookies disabled */
    }
  }

  function readLocal() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== "object") return null;
      return parsed;
    } catch (e) {
      return null;
    }
  }

  function isFresh(stash) {
    if (!stash || !stash.capturedAt) return false;
    var age = Date.now() - Number(stash.capturedAt);
    return age >= 0 && age < TTL_MS;
  }

  function write(stash) {
    // A "direct" result carries no information and must never win for 30 days.
    if (!stash || !stash.channel || stash.channel === "direct") return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stash));
    } catch (e) {
      /* private mode / quota — the cookie below is the fallback */
    }
    writeCookie(stash);
  }

  function readRaw() {
    var local = readLocal();
    if (isFresh(local)) return local;
    var cookie = readCookie();
    if (isFresh(cookie)) {
      // Storage was cleared but the cookie survived; restore the faster path.
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cookie));
      } catch (e) {
        /* no-op */
      }
      return cookie;
    }
    return null;
  }

  function read() {
    return readRaw();
  }

  /** Last paid click wins: a tagged landing replaces whatever was stored. */
  function captureFromLocation() {
    var incoming = parseUrlAttribution(
      typeof location !== "undefined" ? location.search : "",
      typeof location !== "undefined" ? location.href : "",
    );
    if (!incoming || incoming.channel === "direct") return read();

    write(incoming);
    return incoming;
  }

  /** Build COD submit attribution (current URL, else stash, else referrer). */
  function forSubmit() {
    var pageUrl = typeof location !== "undefined" ? location.href : "";
    var referrer = typeof document !== "undefined" ? document.referrer || "" : "";

    var current = parseUrlAttribution(
      typeof location !== "undefined" ? location.search : "",
      pageUrl,
    );
    var stash = current && current.channel !== "direct" ? current : read();

    if (!stash) {
      var referrerChannel = deriveChannelFromReferrer(referrer);
      var inferred = { channel: referrerChannel || "direct" };
      if (referrerChannel) inferred.derived_from = "referrer";
      return {
        source_url: pageUrl,
        page_url: pageUrl,
        referrer: referrer,
        utm: inferred,
        ad_source: inferred.channel,
      };
    }

    var utm = omitEmpty(stash.utm || {});
    if (!utm.channel) utm.channel = deriveAdChannel(utm);
    if (!utm.derived_from) utm.derived_from = "utm";
    return {
      source_url: stash.landingUrl || pageUrl,
      page_url: pageUrl,
      referrer: referrer,
      utm: utm,
      ad_source: stash.channel || utm.channel || "direct",
    };
  }

  try {
    captureFromLocation();
  } catch (e) {
    /* no-op */
  }

  window.SKGAttribution = {
    version: VERSION,
    read: read,
    deriveAdChannel: deriveAdChannel,
    deriveChannelFromReferrer: deriveChannelFromReferrer,
    forSubmit: forSubmit,
    captureFromLocation: captureFromLocation,
  };
})();
